<script lang="ts" setup>
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { debounce } from "@/assets/js/helpers";
import { useConfirm } from "primevue/useconfirm";
import type { IPost } from "@/api/types";
import { useSessionStore } from "@/store/session";
import type { IStatus } from "@/api/types";

definePage({
	name: "EditPosts",
	meta: {
		name: "Edit Posts",
	},
});

const confirm = useConfirm();
const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();
const posts = ref<IPost[] | null>(null);
const draftBool = ref(true);
const publishedBool = ref(true);
const hiddenBool = ref(true);
const latestBool = ref(true);
const scheduledBool = ref(true);
const pst_id = ref<number | null>(null);
const searchValue = ref<string | null>(null);
const numRows = ref(8);
const offsetValue = ref<number | null>(0);
const currentPage = ref(1);
const selectedProduct = ref();
const orderBy = ref([
	{ name: "Latest", code: "LAT" },
	{ name: "Oldest", code: "OLD" },
]);
const loadingDelete = ref(false);
const viewAllPosts = sessionStore.can("view-all-posts");
const viewHidden = sessionStore.can("view-hidden");

async function getPosts() {
	if (searchValue.value && !searchValue.value.trim()) searchValue.value = null;

	const params = {
		draft: draftBool.value,
		published: publishedBool.value,
		latest: latestBool.value,
		hidden: viewHidden ? hiddenBool.value : false,
		scheduled: scheduledBool.value,
		list: true,
		pst_id: pst_id.value,
		search: searchValue.value,
		limit: numRows.value,
		offset: offsetValue.value,
		usr_id: viewAllPosts ? null : sessionStore.userData.usr_id,
		admin_panel: true,
	};

	try {
		const res = await api.postsGet(params);
		posts.value = res.data.data.map((post: IPost) => {
			// Map status name
			const status = store.statuses.find((s) => s.code === post.sta_code);
			post.sta_name = status ? status.name : post.sta_code;

			// Format dates
			post.pst_utc_datetime = store.utcToLocale(post.pst_utc_datetime, true);
			post.pst_utc_updated = post.pst_utc_updated
				? store.utcToLocale(post.pst_utc_updated, true)
				: null;
			post.pst_utc_deleted = post.pst_utc_deleted
				? store.utcToLocale(post.pst_utc_deleted, true)
				: null;

			return post;
		});
		// console.log(posts.value);
	} catch (err: any) {
		console.warn(err.message);
	}
}

async function onPageChange(event: any) {
	currentPage.value = event.page + 1;
	offsetValue.value = numRows.value * currentPage.value - numRows.value;
	await getPosts();
}

const getPostsDebounce = () => {
	debounce(getPosts(), 300, true);
};

async function changeStatus(event: any) {
	draftBool.value = false;
	publishedBool.value = false;
	hiddenBool.value = false;

	if (event) {
		event.forEach((selected: any) => {
			switch (selected.code) {
				case "DRA":
					draftBool.value = true;
					break;
				case "PUB":
					publishedBool.value = true;
					break;
				case "HID":
					hiddenBool.value = true;
					break;
			}
		});
	}

	await getPosts();
}

async function changeOrder(event: any) {
	latestBool.value = true;

	if (event) {
		switch (event.code) {
			case "LAT":
				latestBool.value = true;
				break;
			case "OLD":
				latestBool.value = false;
				break;
		}
	}
	await getPosts();
}

async function deletePost(pst_id: number) {
	if (!sessionStore.can("delete-post")) {
		toast.add({
			severity: "error",
			summary: $t("app.permissions.deletePost.title"),
			detail: $t("app.permissions.deletePost.msg"),
			life: 3000,
		});
		return;
	}

	loadingDelete.value = true;
	const params = {
		pst_id,
	};
	try {
		const res = await api.postsRemove(params);
		toast.add({
			severity: "success",
			summary: $t("app.editPosts.success.title"),
			detail: $t("app.editPosts.success.msg"),
			life: 3000,
		});
		await getPosts();
		console.warn(res.data);
	} catch (err: any) {
		if (
			err.response.data.message.includes("not integer number") &&
			err.response.data.message.includes("required")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.editPosts.error.invalidId.title"),
				detail: $t("app.editPosts.error.invalidId.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Unauthorized")) {
			toast.add({
				severity: "error",
				summary: $t("app.permissions.editPost.title"),
				detail: $t("app.permissions.editPost.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Post not found.")) {
			toast.add({
				severity: "error",
				summary: $t("app.editPosts.error.notFound.title"),
				detail: $t("app.editPosts.error.notFound.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	} finally {
		loadingDelete.value = false;
	}
}

const confirmDelete = (pst_id: number) => {
	confirm.require({
		message: $t("app.editPosts.confirm.deletePost.msg"),
		header: $t("app.editPosts.confirm.deletePost.header"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.delete"),
			severity: "danger",
		},
		accept: () => {
			deletePost(pst_id);
		},
	});
};

const isOptionDisabled = (option: IStatus): boolean => {
	if (!option.permission_view) return false;
	return !sessionStore.can(option.permission_view);
};

onMounted(async () => {
	await getPosts();
});
</script>

<template>
	<div class="admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.editPosts.title") }}
				</div>
				<div class="admin-section-header-filters-wrapper">
					<IconField
						:pt="{
							root: {
								style: 'width: 333px; max-width: 90%; height: 38px;',
							},
						}"
					>
						<InputIcon>
							<i-fa-magnifying-glass />
						</InputIcon>
						<InputText
							v-model="searchValue"
							@value-change="getPostsDebounce"
							:placeholder="$t('app.editPosts.searchPlaceholder')"
							fluid
						/>
					</IconField>
					<MultiSelect
						id="status"
						:options="store.statuses"
						optionLabel="name"
						:placeholder="$t('app.editPosts.statusPlaceholder')"
						:maxSelectedLabels="1"
						@update:modelValue="changeStatus"
						:showClear="true"
						:resetFilterOnClear="true"
						:showToggleAll="false"
						:optionDisabled="isOptionDisabled"
						:pt="{
							root: {
								class: 'edit-posts-filters',
							},
							label: {
								style: 'padding-right: 24px',
							},
						}"
					/>
					<Select
						id="order"
						:options="orderBy"
						optionLabel="name"
						:placeholder="$t('app.editPosts.orderByPlaceholder')"
						:showClear="true"
						:resetFilterOnClear="true"
						:showToggleAll="false"
						@update:modelValue="changeOrder"
						:pt="{
							root: {
								class: 'edit-posts-filters',
							},
							label: {
								style: 'padding-right: 24px',
							},
						}"
					/>
				</div>
			</div>
			<div class="admin-section-content-wrapper">
				<DataTable
					v-if="!posts"
					:value="[
						{ id: 1 },
						{ id: 2 },
						{ id: 3 },
						{ id: 4 },
						{ id: 5 },
						{ id: 6 },
						{ id: 7 },
						{ id: 8 },
					]"
					tableStyle="min-width: 1264px;"
					stripedRows
				>
					<Column
						field="pst_title"
						:header="$t('app.editPosts.columns.titleLabel')"
					>
						<template #body>
							<Skeleton height="29px"></Skeleton>
						</template>
					</Column>
					<Column
						field="usr_displayname"
						:header="$t('app.editPosts.columns.authorLabel')"
						style="width: 200px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						field="pst_utc_updated"
						:header="$t('app.editPosts.columns.updatedLabel')"
						style="width: 200px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						field="pst_utc_datetime"
						:header="$t('app.editPosts.columns.createdLabel')"
						style="width: 200px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						field="sta_name"
						:header="$t('app.editPosts.columns.statusLabel')"
						style="width: 150px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						:header="$t('app.editPosts.columns.editLabel')"
						style="width: 100px"
					>
						<template #body>
							<div>
								<Button
									:label="$t('app.editPosts.columns.editLabel')"
									severity="info"
									variant="outlined"
									style="
										width: 92px;
										height: 29px;
										font-size: 11px;
										font-weight: 700;
										line-height: 15px;
									"
								/>
							</div>
						</template>
					</Column>
					<Column
						:header="$t('app.editPosts.columns.deleteLabel')"
						style="width: 100px"
					>
						<template #body>
							<Button
								:label="$t('app.editPosts.columns.deleteLabel')"
								severity="danger"
								variant="outlined"
								style="
									width: 92px;
									height: 29px;
									font-size: 11px;
									font-weight: 700;
									line-height: 15px;
								"
							/>
						</template>
					</Column>
				</DataTable>
				<div v-if="posts && posts.length < 1">
					{{ $t("app.editPosts.noPosts") }}
				</div>
				<DataTable
					v-else
					:value="posts"
					tableStyle="min-width: 1264px;"
					stripedRows
					selectionMode="single"
					v-model:selection="selectedProduct"
				>
					<Column
						field="pst_title"
						:header="$t('app.editPosts.columns.titleLabel')"
					></Column>
					<Column
						field="usr_displayname"
						:header="$t('app.editPosts.columns.authorLabel')"
						style="width: 200px"
					></Column>
					<Column
						field="pst_utc_updated"
						:header="$t('app.editPosts.columns.updatedLabel')"
						style="width: 200px"
					></Column>
					<Column
						field="pst_utc_datetime"
						:header="$t('app.editPosts.columns.createdLabel')"
						style="width: 200px"
					></Column>
					<Column
						field="sta_name"
						:header="$t('app.editPosts.columns.statusLabel')"
						style="width: 150px"
					></Column>
					<Column
						:header="$t('app.editPosts.columns.editLabel')"
						style="width: 100px"
					>
						<template #body="{ data }">
							<router-link
								:to="{ name: 'EditPost', params: { id: data.pst_id } }"
							>
								<Button
									:label="$t('app.editPosts.columns.editLabel')"
									severity="info"
									variant="outlined"
									style="
										width: 92px;
										height: 29px;
										font-size: 11px;
										font-weight: 700;
										line-height: 15px;
									"
								/>
							</router-link>
						</template>
					</Column>
					<Column
						:header="$t('app.editPosts.columns.deleteLabel')"
						style="width: 100px"
					>
						<template #body="{ data }">
							<Button
								:label="$t('app.editPosts.columns.deleteLabel')"
								severity="danger"
								variant="outlined"
								style="
									width: 92px;
									height: 29px;
									font-size: 11px;
									font-weight: 700;
									line-height: 15px;
								"
								@click="confirmDelete(data.pst_id)"
								:loading="loadingDelete"
							/>
						</template>
					</Column>
				</DataTable>
				<Paginator
					v-if="posts && posts.length > 0"
					:template="{
						'768px': 'PrevPageLink CurrentPageReport NextPageLink',
						default:
							'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink',
					}"
					:rows="numRows"
					:totalRecords="posts[0].total_count"
					@page="onPageChange"
				></Paginator>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-datatable-table-container {
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
}

.p-datatable-column-title {
	font-size: 11px;
	font-weight: 700;
	line-height: 12.89px;
	color: $text-color-lighter;
}

.edit-posts-filters {
	width: 333px !important;
	max-width: 90% !important;

	@media screen and (width >= 1224px) {
		width: 173px !important;
	}
}
</style>
